/**
 * 报名全流程的**五步**口径
 * -----------------------------------------------------------------------------
 * 唯一来源。首页状态卡与提交成功页共用同一份步骤定义，避免两页各写一套导致
 * 「同一张单在两页显示不同进度」。
 *
 * ⚠️ 步骤文案**不含「资料核验 / 审核中」**：核验前移是硬口径（核验在提交前完成），
 * 设计稿里那句「资料核验」照抄会在界面上造出一个不存在的「提交后等核验」状态。
 * `SUBMITTED` 那句提示同理 —— 它只是提交瞬间的瞬态，落库后立即转 `PENDING_PAYMENT`。
 *
 * 第 5 步「领取教材」（2026-10-22 用户拍板）：**报名成功后才有资格领**，没到那一步
 * 一律是灰色未达态（不隐藏）—— 学员要知道「还差最后一步」，藏起来反而像流程走完了。
 * 判「到哪一步」不能只看主状态：教材是**独立列** `materialStatus`（2026-10-22 起落库，
 * 原为派生），同一个 `ENROLLED` 可能还没申请、待发货、在途、或已领取，
 * 所以 `flowProgress` 要同时吃 `materialStatus`。
 *
 * 教材按**报名单**算，不是按账号算：一个账号可以有多张报名单，各自领各自的。
 */
import { MaterialStatus, type RegistrationStatus } from '@enroll/shared';

export const FLOW_STEPS = ['填写信息', '身份核验', '缴费', '报名成功', '领取教材'] as const;

/** 步骤总数。「第 N / X 步」一律取它 —— 别再各处硬编码 4 / 5 */
export const FLOW_STEP_COUNT = FLOW_STEPS.length;

/** 报名单状态 → 当前停留的步骤下标（0 起） */
export const STEP_OF_STATUS: Record<RegistrationStatus, number> = {
  DRAFT: 0,
  SUBMITTED: 2,
  REJECTED: 0,
  PENDING_PAYMENT: 2,
  ENROLLED: 3,
  REFUNDED: 3,
  VOIDED: 2,
};

export function stepOfStatus(status: RegistrationStatus): number {
  return STEP_OF_STATUS[status] ?? 0;
}

/** 教材环节**已收到**的状态；其余（含异常）都还算「没到手」 */
const MATERIAL_RECEIVED: readonly string[] = [MaterialStatus.RECEIVED];

export interface FlowProgress {
  /** 传给 `ProgressRail` 的 `current`：0~4 停在某一格；等于 `FLOW_STEP_COUNT` 表示全走完 */
  railCurrent: number;
  /** 「第 N 步」的 N（1 起，封顶 `FLOW_STEP_COUNT`） */
  stepNo: number;
  /** 5 步全部完成（进度条整条点亮） */
  complete: boolean;
}

/**
 * 报名单 → 进度条的三个数。
 *
 * 只有 `ENROLLED` 才可能踩到第 5 步：`REFUNDED` 虽然停在「报名成功」那一格，
 * 但它已经出局了，不该再提示去领教材（`complete` 恒为假，后续步骤保持灰色）。
 */
export function flowProgress(
  status: RegistrationStatus,
  materialStatus?: string | null,
): FlowProgress {
  const lastIndex = FLOW_STEP_COUNT - 1;
  const base = stepOfStatus(status);
  if (status !== 'ENROLLED') {
    return { railCurrent: base, stepNo: Math.min(base, lastIndex) + 1, complete: false };
  }
  const received = MATERIAL_RECEIVED.includes(materialStatus ?? '');
  return received
    ? { railCurrent: FLOW_STEP_COUNT, stepNo: FLOW_STEP_COUNT, complete: true }
    : { railCurrent: lastIndex, stepNo: FLOW_STEP_COUNT, complete: false };
}

/**
 * 教材环节的一句话状态（进度条 / 列表胶囊用）。
 *
 * 与 `MATERIAL_STATUS_LABEL` 的差别只在措辞视角：站在**学员**这边，
 * 「待申请领取」是「该你去申请了」，说成「未发放」会让人以为只能等学校。
 */
export function materialStepText(materialStatus?: string | null): string {
  switch (materialStatus) {
    case MaterialStatus.PENDING_SHIP:
      return '待发货';
    case MaterialStatus.SHIPPED:
      return '已发货';
    case MaterialStatus.RECEIVED:
      return '已领取';
    case MaterialStatus.ABNORMAL:
      return '发放异常';
    default:
      return '待申请领取';
  }
}

/**
 * 学员在教材环节**下一步该做什么**（领取页按钮文案的唯一来源）。
 * `null` = 不需要动作（已领取 / 异常走客服）。
 */
export function materialActionText(materialStatus?: string | null): string | null {
  switch (materialStatus) {
    case MaterialStatus.PENDING_APPLY:
    case undefined:
    case null:
    case '':
      return '领取';
    case MaterialStatus.PENDING_SHIP:
      return '已提交领取';
    case MaterialStatus.SHIPPED:
      return '运输中';
    default:
      return null;
  }
}

/** 是否需要「领取教材」这一步：只有报名成功（且未退费作废）的单子才有 */
export function needsMaterialStep(status: RegistrationStatus): boolean {
  return status === 'ENROLLED';
}
